import { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, MapPin, ListChecks, ChartBar, Bell } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="card" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-[#52525B] mb-1">{label}</p>
          <p className="heading text-3xl font-black text-[#09090B]">{value}</p>
        </div>
        <div className={`p-3 rounded-md ${color}`}>
          <Icon size={24} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('warehouse_token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-zinc-200 rounded w-64"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-zinc-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="dashboard">
      <div className="mb-8">
        <h1 className="heading text-3xl font-black text-[#09090B] mb-2" data-testid="dashboard-title">
          Dashboard Overview
        </h1>
        <p className="text-[#52525B]">Real-time warehouse metrics and insights</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Package}
          label="Total Items"
          value={stats?.total_items || 0}
          color="bg-[#007AFF]/10 text-[#007AFF]"
          testId="stat-total-items"
        />
        <StatCard
          icon={MapPin}
          label="Total Locations"
          value={stats?.total_locations || 0}
          color="bg-[#16A34A]/10 text-[#16A34A]"
          testId="stat-total-locations"
        />
        <StatCard
          icon={ListChecks}
          label="Pending Tasks"
          value={stats?.pending_tasks || 0}
          color="bg-[#F59E0B]/10 text-[#F59E0B]"
          testId="stat-pending-tasks"
        />
        <StatCard
          icon={Bell}
          label="Low Stock Alerts"
          value={stats?.low_stock_alerts || 0}
          color="bg-[#FF3B30]/10 text-[#FF3B30]"
          testId="stat-low-stock-alerts"
        />
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="heading text-xl font-bold text-[#09090B]" data-testid="capacity-title">
              Warehouse Capacity
            </h3>
            <p className="text-sm text-[#52525B]">Overall utilization</p>
          </div>
          <div className="text-right">
            <p className="heading text-3xl font-black text-[#09090B]" data-testid="capacity-value">
              {stats?.capacity_utilization?.toFixed(1) || 0}%
            </p>
          </div>
        </div>
        <div className="w-full bg-[#E4E4E7] rounded-full h-6 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              (stats?.capacity_utilization || 0) > 80
                ? 'bg-[#DC2626]'
                : (stats?.capacity_utilization || 0) > 50
                ? 'bg-[#F59E0B]'
                : 'bg-[#16A34A]'
            }`}
            style={{ width: `${stats?.capacity_utilization || 0}%` }}
            data-testid="capacity-bar"
          ></div>
        </div>
      </div>

      {user.role === 'picker' && (
        <div className="mt-8">
          <div className="card bg-[#007AFF]/5 border-[#007AFF]/20">
            <h3 className="heading text-lg font-bold text-[#09090B] mb-2">Quick Action</h3>
            <p className="text-[#52525B] mb-4">View and complete your picking tasks</p>
            <a
              href="/picking"
              className="inline-block btn-primary"
              data-testid="quick-action-picking"
            >
              Go to Picking Tasks
            </a>
          </div>
        </div>
      )}

      {user.role === 'stocker' && (
        <div className="mt-8">
          <div className="card bg-[#16A34A]/5 border-[#16A34A]/20">
            <h3 className="heading text-lg font-bold text-[#09090B] mb-2">Quick Action</h3>
            <p className="text-[#52525B] mb-4">View and complete your put away tasks</p>
            <a
              href="/putaway"
              className="inline-block btn-primary"
              data-testid="quick-action-putaway"
            >
              Go to Put Away Tasks
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
